import { GatsbyBrowser } from "gatsby";

import { legacyLogicalPropertiesTransformer, StyleProvider } from "@ant-design/cssinjs";

import "./src/pages/globals.scss";
import "./fonts/axiforma/stylesheet.css";
import "./fonts/roboto/stylesheet.css";
import "./src/dayjs-setup";

// reach-router's `location` carries no navigation action, so we detect
// browser back/forward/reload from two different signals, depending on how
// the current document was reached:
// - An in-document transition (the header's Gatsby `<Link>`) never reloads
//   the document, so `popstate` is the only trace it leaves.
// - A full document load (the footer's plain `<a href>` anchors) starts a
//   brand new document, so there is no `popstate` at all - the Navigation
//   Timing API is what tells us this document was reached via back/forward
//   (or reload) instead of a link.
// Either way, a link click is a new visit and should start at the top; only
// back, forward, and reload should restore where the visitor was.
let isPopNavigation = false;

if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    isPopNavigation = true;
  });
}

// The Navigation Timing entry's type never changes for the lifetime of a
// document, so it can only be trusted on the first scroll decision - past
// that point, without this guard, every later in-document link click in a
// document that itself loaded via back/forward would be mistaken for
// another traversal.
let isFirstDecision = true;

function wasHistoryTraversal(): boolean {
  if (isPopNavigation) {
    return true;
  }

  if (!isFirstDecision || typeof window === "undefined" || !window.performance) {
    return false;
  }

  const entries = window.performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
  const navigationType = entries[0]?.type;

  return navigationType === "back_forward" || navigationType === "reload";
}

export const shouldUpdateScroll: GatsbyBrowser["shouldUpdateScroll"] = ({
  routerProps: { location },
  getSavedScrollPosition,
}) => {
  if (location.state && location.state.preventScroll === true) {
    return false;
  }

  // Both signals are consumed by exactly one scroll decision, so read and
  // reset them before any branch returns - otherwise going back to a URL with
  // a hash would leave a flag set for whatever the visitor clicks next.
  const isBackForward = wasHistoryTraversal();
  isPopNavigation = false;
  isFirstDecision = false;

  if (location.hash) {
    window.history.scrollRestoration = "auto";
    return true;
  }

  window.history.scrollRestoration = "manual";

  const currentPosition = getSavedScrollPosition(location);
  const top = isBackForward ? (currentPosition ? currentPosition[1] : 0) : 0;

  window.setTimeout(() => {
    window.requestAnimationFrame(() => {
      try {
        window.scrollTo({ top, behavior: "instant" });
      } catch {
        window.scrollTo(top);
      }
    });
  }, 0);

  return false;
};

export const wrapRootElement: GatsbyBrowser["wrapRootElement"] = ({ element }) => {
  return (
    <StyleProvider hashPriority="high" transformers={[legacyLogicalPropertiesTransformer]}>
      {element}
    </StyleProvider>
  );
};
